import { Separator } from "@/components/ui/separator";
import { BlockActions } from "./BlockActions";
import { BlockBreadcrumbNav } from "./BlockBreadcrumbNav";
import { BlockSettingsPopover } from "./BlockSettingsPopover";
import { BlockStyleControls } from "./BlockStyleControls";
import { TextMarkControls } from "./TextMarkControls";
import { UndoRedoControls } from "./UndoRedoControls";
import type { DocumentEditorApi } from "./useDocumentEditor";

interface DocumentFormatToolbarProps {
  /** The editor whose text session and selection the toolbar formats. */
  editor: DocumentEditorApi;
  /** Disables every control (an image upload is in flight). */
  disabled: boolean;
  /** Registers a freshly uploaded asset id and its file for instant preview. */
  onLocalAsset: (assetId: string, file: File) => void;
  /** Reports whether an image upload is in flight. */
  onUploadingChange: (isUploading: boolean) => void;
}

/**
 * The persistent bar above the canvas, shared by the per-quote and template
 * editors: the whole block inspector lives here, so the canvas gets the full
 * width. History sits leftmost, character formatting drives the open inline
 * text session, block styles drive the selected block, the "Properties"
 * popover holds the selected block's settings (visibility, field source,
 * image, repeater), and the block operations sit at the right edge. The
 * flexible middle carries the selected block's breadcrumb - naming the
 * selection and reaching enclosing blocks and nested repeaters a canvas click
 * cannot - including while an inline text session is open, since the edited
 * block is still the selection. The block style knobs stay live during a text
 * session too: a knob's mousedown keeps the inline editor focused and the
 * change commits the session before it applies. Whichever group has no target
 * is disabled rather than unmounted so the bar never shifts under the pointer.
 * @param editor - The editor whose text session and selection are formatted
 * @param disabled - Disables every control
 * @param onLocalAsset - Registers an uploaded asset id and file for preview
 * @param onUploadingChange - Reports whether an image upload is in flight
 * @returns The formatting toolbar
 */
export function DocumentFormatToolbar({
  editor,
  disabled,
  onLocalAsset,
  onUploadingChange,
}: DocumentFormatToolbarProps) {
  const session = editor.textEdit;
  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex min-w-0 flex-1 flex-wrap items-center gap-1"
    >
      <UndoRedoControls editor={editor} disabled={disabled} />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <TextMarkControls
        editor={editor.activeTextEditor}
        block={editor.selection}
        previewRef={editor.previewRef}
        blocksEnabled={session?.blocksEnabled ?? false}
        disabled={disabled}
        onStyleChange={editor.setStyle}
        onCommitSession={editor.commitTextEdit}
      />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <BlockStyleControls
        block={editor.selection}
        disabled={disabled}
        onStyleChange={(property, value) => {
          editor.commitTextEdit();
          editor.setStyle(property, value);
        }}
      />
      <Separator orientation="vertical" className="mx-1 h-5" />
      <BlockSettingsPopover
        editor={editor}
        disabled={disabled}
        onLocalAsset={onLocalAsset}
        onUploadingChange={onUploadingChange}
      />
      <div className="flex min-w-0 flex-1 items-center px-2">
        {editor.selection !== null && (
          <BlockBreadcrumbNav
            ancestors={editor.selection.ancestors}
            current={editor.selection.label}
            nested={editor.selection.repeater?.children ?? []}
            onSelectNode={editor.selectNode}
          />
        )}
      </div>
      <Separator orientation="vertical" className="mx-1 h-5" />
      <BlockActions editor={editor} disabled={disabled} />
    </div>
  );
}
